import { useGetAllDepartmentsQuery } from "@/app/features/employee/emp-dashboard/emp-dashboard.api";
import { useGetAllAdminKPIPlanQuery } from "@/app/features/employee/performance/performance.api";
import { FILTER_KEYS } from "@/constants/keys.constants";
import { useAppFilters, useSelectionOptions } from "@/hooks";
import useAuth from "@/hooks/useAuth";
import { ChevronDownIcon } from "@/icons";
import { Flex, Select } from "antd";
import type { LineManagerAppraisalSegmentType } from "../LineManagerAppraisal";
import type { LineManagerSegmentType } from "../LineManagerKPI";

type AllSegmentTypes = LineManagerSegmentType | LineManagerAppraisalSegmentType;

// ──────── Shared Filter Options ────────
const PLAN_TYPES = [
  { label: "Monthly", value: "Monthly" },
  { label: "Quarterly", value: "Quarterly" },
  { label: "Half-Yearly", value: "Half-Yearly" },
  { label: "Yearly", value: "Yearly" },
];

const KPI_STATUSES = [
  { label: "Draft", value: "DRAFT" },
  { label: "Pending", value: "PENDING" },
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
];

const MANAGER_TYPES = [
  { label: "L1 Manager", value: "L1" },
  { label: "L2 Manager", value: "L2" },
  { label: "L3 Manager", value: "L3" },
  { label: "L4 Manager", value: "L4" },
  { label: "L5 Manager", value: "L5" },
];

const TeamKPIFilters = ({ filterKey }: { filterKey: string }) => {
  const { organizationId } = useAuth();
  const { handleFilter } = useAppFilters(filterKey);
  const [departmentOptions, isDeptLoading] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: "name",
      valueKey: "id",
      dataKey: "departments",
      params: { organization_id: organizationId },
    },
  );

  const [planOptions, isPlanLoading] = useSelectionOptions(
    useGetAllAdminKPIPlanQuery,
    {
      labelKey: "planName",
      valueKey: "id",
      dataKey: "plans",
      params: { organization_id: organizationId },
    },
  );

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <div className="min-w-32">
          <Select
            allowClear
            size="large"
            showSearch
            optionFilterProp="label"
            placement="topRight"
            popupMatchSelectWidth={false}
            loading={isPlanLoading as boolean}
            placeholder="Plan"
            suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
            options={planOptions as any[]}
            onChange={(value) => handleFilter("plan_id", value)}
          />
        </div>
        <div className="min-w-32">
          <Select
            allowClear
            size="large"
            placeholder="Manager Type"
            suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
            options={MANAGER_TYPES}
            onChange={(value) => handleFilter("manager_type", value)}
          />
        </div>
        <div className="min-w-40">
          <Select
            allowClear
            size="large"
            showSearch
            optionFilterProp="label"
            placement="topRight"
            popupMatchSelectWidth={false}
            loading={isDeptLoading as boolean}
            placeholder="Department"
            suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={departmentOptions as any[]}
            onChange={(value) => handleFilter("department_id", value)}
          />
        </div>
        <div className="min-w-32">
          <Select
            allowClear
            size="large"
            placeholder="Status"
            suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
            options={KPI_STATUSES}
            onChange={(value) => handleFilter("kpi_status", value)}
          />
        </div>
      </Flex>
    </div>
  );
};

const OwnAppraisalFilters = ({ filterKey }: { filterKey: string }) => {
  const { organizationId } = useAuth();
  const { handleFilter } = useAppFilters(filterKey);
  const [departmentOptions, isDeptLoading] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: "name",
      valueKey: "id",
      dataKey: "departments",
      params: { organization_id: organizationId },
    },
  );

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Select
          allowClear
          size="large"
          showSearch
          optionFilterProp="label"
          placement="topRight"
          popupMatchSelectWidth={false}
          placeholder="Plan Type"
          style={{ width: 140 }}
          suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
          options={PLAN_TYPES}
          onChange={(value) => handleFilter("type", value)}
        />
        <Select
          allowClear
          size="large"
          showSearch
          optionFilterProp="label"
          placement="topRight"
          popupMatchSelectWidth={false}
          loading={isDeptLoading as boolean}
          placeholder="Department"
          style={{ width: 160 }}
          suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
          options={departmentOptions as any[]}
          onChange={(value) => handleFilter("department_id", value)}
        />
        <Select
          allowClear
          size="large"
          placeholder="Status"
          style={{ width: 140 }}
          suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
          options={KPI_STATUSES}
          onChange={(value) => handleFilter("status", value)}
        />
      </Flex>
    </div>
  );
};

const TeamAppraisalFilters = ({ filterKey }: { filterKey: string }) => {
  const { organizationId } = useAuth();
  const { handleFilter } = useAppFilters(filterKey);
  const [departmentOptions, isDeptLoading] = useSelectionOptions(
    useGetAllDepartmentsQuery,
    {
      labelKey: "name",
      valueKey: "id",
      dataKey: "departments",
      params: { organization_id: organizationId },
    },
  );

  return (
    <div className="flex flex-col items-end gap-4">
      <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
        <Select
          allowClear
          size="large"
          placeholder="Manager Type"
          style={{ width: 140 }}
          suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
          options={MANAGER_TYPES}
          onChange={(value) => handleFilter("manager_type", value)}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placement="topRight"
          popupMatchSelectWidth={false}
          size="large"
          loading={isDeptLoading as boolean}
          placeholder="Department"
          style={{ width: 160 }}
          suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
          options={departmentOptions as any[]}
          onChange={(value) => handleFilter("department_id", value)}
        />
        <Select
          allowClear
          size="large"
          placeholder="Status"
          style={{ width: 140 }}
          suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
          options={KPI_STATUSES}
          onChange={(value) => handleFilter("status", value)}
        />
      </Flex>
    </div>
  );
};

type Props = { type: AllSegmentTypes };

const Filters = ({ type }: Props) => {
  switch (type) {
    case "own-kpi":
      return null;
    case "own-appraisal":
      return <OwnAppraisalFilters filterKey={FILTER_KEYS.EMP_OWN_APPRAISAL} />;
    case "team-kpi":
      return <TeamKPIFilters filterKey={FILTER_KEYS.LM_TEAM_KPI} />;
    case "team-appraisal":
      return <TeamAppraisalFilters filterKey={FILTER_KEYS.LM_TEAM_APPRAISAL} />;
    default:
      return null;
  }
};

export default Filters;
